"use client";

import { useEffect, useRef, useState } from "react";
import { GraduationCap, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface DegreeAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  id?: string;
  required?: boolean;
}

/**
 * Autocomplete picker for academic credentials backed by the canonical
 * US/Canada degree list at `GET /api/degrees/list`. Users can:
 *
 * 1. Type to filter the list and click an option, or
 * 2. Type a custom degree that isn't in the list and submit it as-is.
 *
 * The component never blocks free-text — the `onChange` callback always
 * fires with whatever the user has typed. Submission-side validation is
 * the caller's responsibility (e.g. require non-empty).
 */
export function DegreeAutocomplete({
  value,
  onChange,
  placeholder = "Search or type your degree…",
  className = "",
  inputClassName,
  id,
  required,
}: DegreeAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [allDegrees, setAllDegrees] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/api/degrees/list")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && Array.isArray(data.degrees)) {
          setAllDegrees(data.degrees);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const trimmed = inputValue.trim();
  const filtered =
    trimmed.length === 0
      ? allDegrees
      : allDegrees.filter((d) =>
          d.toLowerCase().includes(trimmed.toLowerCase()),
        );

  // Hide the "Use as typed" hint when the user has typed something that
  // already exactly matches a known degree (case-insensitive).
  const exactMatch =
    trimmed.length > 0 &&
    allDegrees.some((d) => d.toLowerCase() === trimmed.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    onChange(v);
    setIsOpen(true);
  };

  const handleSelect = (deg: string) => {
    setInputValue(deg);
    onChange(deg);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange("");
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") setIsOpen(false);
    if (e.key === "Enter" && isOpen) {
      // Pressing Enter selects the first filtered option if any; otherwise
      // the typed value is already committed via onChange. Always prevent
      // form submission from this input.
      e.preventDefault();
      if (filtered.length > 0) handleSelect(filtered[0]);
      else setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={["pl-10 pr-10 bg-white", inputClassName].filter(Boolean).join(" ")}
          autoComplete="off"
          required={required}
        />
        {inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
            onClick={handleClear}
            aria-label="Clear degree"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-[80] w-full mt-1 bg-white text-gray-900 border border-border rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          {filtered.length > 0 ? (
            filtered.slice(0, 60).map((deg) => (
              <button
                key={deg}
                type="button"
                className="w-full px-4 py-2 text-left text-gray-900 hover:bg-muted focus:bg-muted focus:outline-none text-sm flex items-center gap-2 border-b border-border last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(deg);
                }}
              >
                <GraduationCap className="h-3 w-3 text-muted-foreground shrink-0" />
                {deg}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              No matches found.
            </div>
          )}

          {trimmed.length > 0 && !exactMatch && (
            <button
              type="button"
              className="w-full px-4 py-2 text-left text-sm border-t border-border bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium flex items-center gap-2"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(trimmed);
              }}
            >
              Use &ldquo;{trimmed}&rdquo; (custom)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
